import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { api, ApiError } from '../lib/api.js';
import { formatDateOnly } from '../lib/dateUtils.js';
import { ShortCodeTag } from './ShortCodeTag.js';
import { Pagination } from './Pagination.js';
import { CompanyDrillDownView } from './CompanyDrillDownView.js';
import { CompanyHoverBadge } from './CompanyHoverBadge.js';
import type { CompanyAnalyticsItem, CompanyAnalyticsSummary, CompanyAnalyticsFilterParams } from '@gng-ehs/shared';

const FINANCIAL_YEARS = [
  { value: 'ALL', label: 'All Financial Years' },
  { value: '2026', label: 'FY 2026-27 (Apr 2026 – Mar 2027)' },
  { value: '2025', label: 'FY 2025-26 (Apr 2025 – Mar 2026)' },
  { value: '2024', label: 'FY 2024-25 (Apr 2024 – Mar 2025)' },
  { value: '2023', label: 'FY 2023-24 (Apr 2023 – Mar 2024)' },
  { value: '2022', label: 'FY 2022-23 (Apr 2022 – Mar 2023)' },
];

export function CompanyAnalyticsView() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [certType, setCertType] = useState<'ALL' | 'TRAINING' | 'FORM_9' | 'FORM_10' | 'FORM_11'>('ALL');
  const [financialYear, setFinancialYear] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE'>('ALL');
  const [sortBy, setSortBy] = useState<'total' | 'form9' | 'form10' | 'form11' | 'training' | 'expiring' | 'overdue' | 'company_name'>('total');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [items, setItems] = useState<CompanyAnalyticsItem[]>([]);
  const [summary, setSummary] = useState<CompanyAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Client-side pagination for long lists
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    setError('');

    const params: CompanyAnalyticsFilterParams = {
      cert_type: certType,
      financial_year: financialYear !== 'ALL' ? financialYear : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      search: searchQuery || undefined,
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    api.dashboard
      .companyAnalytics(params)
      .then((res) => {
        if (res.data) {
          setItems(res.data);
          setSummary(res.summary || null);
          setPage(1);
        }
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to load company analytics');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [certType, financialYear, startDate, endDate, searchQuery, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Handle FY Change: auto-set or clear dates
  const handleFinancialYearChange = (fy: string) => {
    setFinancialYear(fy);
    if (fy === 'ALL') {
      setStartDate('');
      setEndDate('');
    } else {
      const year = parseInt(fy, 10);
      setStartDate(`${year}-04-01`);
      setEndDate(`${year + 1}-03-31`);
    }
  };

  // Quick Date Preset Handler
  const handleQuickPreset = (preset: '30d' | '90d' | 'month' | 'fy' | 'all') => {
    const now = new Date();
    if (preset === 'all') {
      setFinancialYear('ALL');
      setStartDate('');
      setEndDate('');
    } else if (preset === '30d') {
      setFinancialYear('ALL');
      const past = new Date(now.getTime() - 30 * 86400000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(now.toISOString().slice(0, 10));
    } else if (preset === '90d') {
      setFinancialYear('ALL');
      const past = new Date(now.getTime() - 90 * 86400000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(now.toISOString().slice(0, 10));
    } else if (preset === 'month') {
      setFinancialYear('ALL');
      const y = now.getFullYear();
      const m = now.getMonth();
      const first = new Date(y, m, 1).toISOString().slice(0, 10);
      const last = new Date(y, m + 1, 0).toISOString().slice(0, 10);
      setStartDate(first);
      setEndDate(last);
    } else if (preset === 'fy') {
      const m = now.getMonth();
      const currentFyStart = m >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      setFinancialYear(String(currentFyStart));
      setStartDate(`${currentFyStart}-04-01`);
      setEndDate(`${currentFyStart + 1}-03-31`);
    }
  };

  // Sort toggle handler
  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!items.length) return;
    const headers = [
      'Company Name',
      'Short Code',
      'Contact Person',
      'Communication Email',
      'Form 9 Count',
      'Form 10 Count',
      'Form 11 Count',
      'Training Count',
      'Total Certificates',
      'Compliant Count',
      'Expiring Soon Count',
      'Overdue Count',
      'Latest Activity Date',
    ];

    const rows = items.map((c) => [
      `"${(c.company_name || '').replace(/"/g, '""')}"`,
      `"${c.short_code || ''}"`,
      `"${(c.contact_person || '').replace(/"/g, '""')}"`,
      `"${c.communication_email || ''}"`,
      c.form9_count,
      c.form10_count,
      c.form11_count,
      c.training_count,
      c.total_count,
      c.compliant_count,
      c.expiring_soon_count,
      c.overdue_count,
      c.latest_activity_date ? formatDateOnly(c.latest_activity_date) : '',
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    a.download = `Company_Analytics_Summary_${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Paginated items
  const paginatedItems = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  }, [items, page, pageSize]);

  const totalPages = Math.ceil(items.length / pageSize);

  // If a company is selected for drill-down, render the Company Level View
  if (selectedCompanyId) {
    const selectedComp = items.find((c) => c.company_id === selectedCompanyId);
    return (
      <CompanyDrillDownView
        companyId={selectedCompanyId}
        initialCompany={selectedComp}
        onBack={() => setSelectedCompanyId(null)}
        defaultFinancialYear={financialYear}
        defaultStartDate={startDate}
        defaultEndDate={endDate}
        defaultCertType={certType}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* ── KPI Metric Summary Cards ────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-4 border-l-4 border-l-blue-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Companies</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.total_companies}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">With matching certificates</p>
              </div>
              <span className="text-2xl p-2 rounded-xl bg-blue-50 text-blue-600">🏢</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="card p-4 border-l-4 border-l-indigo-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Certificates</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{summary.grand_total}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {certType === 'ALL'
                    ? `F9: ${summary.total_form9} • F10: ${summary.total_form10} • F11: ${summary.total_form11} • Trn: ${summary.total_training}`
                    : certType === 'TRAINING'
                    ? `${summary.total_training} Training certs`
                    : certType === 'FORM_9' ? `${summary.total_form9} Form 9 certs` : certType === 'FORM_10'
                    ? `${summary.total_form10} Form 10 certs`
                    : `${summary.total_form11} Form 11 reports`}
                </p>
              </div>
              <span className="text-2xl p-2 rounded-xl bg-indigo-50 text-indigo-600">📜</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card p-4 border-l-4 border-l-emerald-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Compliant / Valid</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.total_compliant}</p>
                <p className="text-[11px] text-emerald-700 font-medium mt-0.5">
                  {summary.grand_total > 0
                    ? `${Math.round((summary.total_compliant / summary.grand_total) * 100)}% compliance rate`
                    : '100% compliant'}
                </p>
              </div>
              <span className="text-2xl p-2 rounded-xl bg-emerald-50 text-emerald-600">🟢</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="card p-4 border-l-4 border-l-amber-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Expiring Soon</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{summary.total_expiring_soon}</p>
                <p className="text-[11px] text-amber-700 font-medium mt-0.5">Due in &le; 30 days</p>
              </div>
              <span className="text-2xl p-2 rounded-xl bg-amber-50 text-amber-600">⚠️</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card p-4 border-l-4 border-l-red-500 bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Overdue / Expired</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{summary.total_overdue}</p>
                <p className="text-[11px] text-red-700 font-medium mt-0.5">Immediate renewal needed</p>
              </div>
              <span className="text-2xl p-2 rounded-xl bg-red-50 text-red-600">🔴</span>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Filter & Control Bar ───────────────────────────────── */}
      <div className="card p-4 sm:p-5 space-y-4 bg-white shadow-sm border border-gray-200">
        {/* Certificate Type Segmented Pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider mr-1">Section:</span>
            <button
              type="button"
              onClick={() => setCertType('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                certType === 'ALL'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Types
            </button>
            <button
              type="button"
              onClick={() => setCertType('TRAINING')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'TRAINING'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/80'
              }`}
            >
              <span>🎓</span>
              <span>Training Certificates</span>
            </button>
            <button
              type="button"
              onClick={() => setCertType('FORM_9')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_9'
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-brand-50 text-brand-800 hover:bg-brand-100 border border-brand-200/80'
              }`}
            >
              <span>📋</span>
              <span>Form 9 (Hoists & Lifts)</span>
            </button><button
              type="button"
              onClick={() => setCertType('FORM_10')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_10'
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-brand-50 text-brand-800 hover:bg-brand-100 border border-brand-200/80'
              }`}
            >
              <span>📋</span>
              <span>Form 10 (Lifting Equip)</span>
            </button>
            <button
              type="button"
              onClick={() => setCertType('FORM_11')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_11'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-purple-50 text-purple-800 hover:bg-purple-100 border border-purple-200/80'
              }`}
            >
              <span>🧪</span>
              <span>Form 11 (Pressure Vessels)</span>
            </button>
          </div>

          {/* Export Action */}
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={!items.length}
            className="btn btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-40"
            title="Download company analytics as CSV"
          >
            <span>📥</span>
            <span>Export CSV</span>
          </button>
        </div>

        {/* Date Filters & Search Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {/* Financial Year Selector */}
          <div className="md:col-span-3">
            <label className="label text-xs font-medium text-gray-700">Financial Year</label>
            <select
              value={financialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              className="input text-xs py-1.5 font-medium"
            >
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy.value} value={fy.value}>
                  {fy.label}
                </option>
              ))}
            </select>
          </div>

          {/* Calendar Date Range Pickers */}
          <div className="md:col-span-4 grid grid-cols-2 gap-2">
            <div>
              <label className="label text-xs font-medium text-gray-700">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setFinancialYear('ALL');
                }}
                className="input text-xs py-1.5"
              />
            </div>
            <div>
              <label className="label text-xs font-medium text-gray-700">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setFinancialYear('ALL');
                }}
                className="input text-xs py-1.5"
              />
            </div>
          </div>

          {/* Search by Company */}
          <div className="md:col-span-3">
            <label className="label text-xs font-medium text-gray-700">Search Company</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or code..."
                className="input text-xs py-1.5 pr-7"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Compliance Status Filter */}
          <div className="md:col-span-2">
            <label className="label text-xs font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="input text-xs py-1.5"
            >
              <option value="ALL">All Statuses</option>
              <option value="COMPLIANT">🟢 Valid Only</option>
              <option value="EXPIRING_SOON">⚠️ Expiring Soon</option>
              <option value="OVERDUE">🔴 Overdue Only</option>
            </select>
          </div>
        </div>

        {/* Quick Date Presets Row */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-gray-500">
          <span className="font-semibold text-gray-600 mr-1">Quick Intervals:</span>
          <button
            type="button"
            onClick={() => handleQuickPreset('month')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors"
          >
            This Month
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('30d')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors"
          >
            Last 30 Days
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('90d')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors"
          >
            Last 90 Days
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('fy')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors"
          >
            Current FY
          </button>
          {(startDate || endDate || financialYear !== 'ALL') && (
            <button
              type="button"
              onClick={() => handleQuickPreset('all')}
              className="px-2 py-0.5 rounded bg-red-50 hover:bg-red-100 text-red-600 text-[11px] font-medium transition-colors ml-1 cursor-pointer"
            >
              Clear Filters ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Error Banner ────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex items-center justify-between">
          <span>⚠️ {error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 font-bold ml-2">✕</button>
        </div>
      )}

      {/* ── Company Analytics Data Table ───────────────────────── */}
      <div className="card overflow-hidden bg-white shadow-sm border border-gray-200 rounded-xl">
        <div className="p-4 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">Company Breakdown</span>
            <span className="text-xs bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded-full font-semibold">
              {items.length} {items.length === 1 ? 'company' : 'companies'}
            </span>
          </div>
          <span className="text-[11px] text-gray-500">
            Click any company name or "Drill Down →" to inspect its certificates
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="text-3xl mb-2">🏢</p>
            <p className="font-semibold text-gray-800 text-sm">No companies found</p>
            <p className="text-xs text-gray-400 mt-1">
              No certificates match the selected certificate type, date range, or search criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto no-scrollbar">
            <table className="table w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <tr>
                  <th
                    onClick={() => handleSort('company_name')}
                    className="py-3 px-4 text-left cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-1">
                      <span>Company Name</span>
                      {sortBy === 'company_name' && (
                        <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('form9')}
                    className={`py-3 px-3 text-center cursor-pointer hover:bg-gray-100 transition-colors ${
                      certType === 'FORM_9' ? 'bg-brand-50 text-brand-900 font-bold' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Form 9</span>
                      {sortBy === 'form9' && <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  </th><th
                    onClick={() => handleSort('form10')}
                    className={`py-3 px-3 text-center cursor-pointer hover:bg-gray-100 transition-colors ${
                      certType === 'FORM_10' ? 'bg-brand-50 text-brand-900 font-bold' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Form 10</span>
                      {sortBy === 'form10' && <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('form11')}
                    className={`py-3 px-3 text-center cursor-pointer hover:bg-gray-100 transition-colors ${
                      certType === 'FORM_11' ? 'bg-purple-50 text-purple-900 font-bold' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Form 11</span>
                      {sortBy === 'form11' && <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('training')}
                    className={`py-3 px-3 text-center cursor-pointer hover:bg-gray-100 transition-colors ${
                      certType === 'TRAINING' ? 'bg-emerald-50 text-emerald-900 font-bold' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Training</span>
                      {sortBy === 'training' && <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total')}
                    className="py-3 px-3 text-center cursor-pointer hover:bg-gray-100 transition-colors font-bold text-gray-900"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Total</span>
                      {sortBy === 'total' && <span>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  </th>
                  <th className="py-3 px-3 text-left">Compliance Status</th>
                  <th className="py-3 px-3 text-left">Latest Activity</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedItems.map((comp) => (
                  <tr key={comp.company_id} className="hover:bg-slate-50/80 transition-colors">
                    {/* Company Column */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedCompanyId(comp.company_id)}
                          className="font-bold text-gray-900 hover:text-brand-600 transition-colors cursor-pointer text-left"
                          title="Click to drill down into company certificates"
                        >
                          {comp.company_name}
                        </button>
                        {comp.short_code && (
                          <CompanyHoverBadge
                            shortCode={comp.short_code}
                            companyId={comp.company_id}
                            company={{
                              id: comp.company_id,
                              short_code: comp.short_code,
                              company_name: comp.company_name,
                              factory_address: comp.factory_address,
                              contact_person: comp.contact_person,
                              communication_email: comp.communication_email,
                            }}
                          >
                            <ShortCodeTag code={comp.short_code} showIcon={false} />
                          </CompanyHoverBadge>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 truncate max-w-xs mt-0.5">
                        {comp.factory_address || comp.communication_email || 'No location set'}
                      </p>
                    </td>

                    {/* Form 10 Count */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {comp.form9_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCertType('FORM_9');
                            setSelectedCompanyId(comp.company_id);
                          }}
                          className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors cursor-pointer"
                          title="Drill down to Form 9 certificates for this company"
                        >
                          {comp.form9_count}
                        </button>
                      ) : (
                        <span className="text-gray-300 font-mono">0</span>
                      )}
                    </td><td className="py-3 px-3 text-center whitespace-nowrap">
                      {comp.form10_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCertType('FORM_10');
                            setSelectedCompanyId(comp.company_id);
                          }}
                          className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors cursor-pointer"
                          title="Drill down to Form 10 certificates for this company"
                        >
                          {comp.form10_count}
                        </button>
                      ) : (
                        <span className="text-gray-300 font-mono">0</span>
                      )}
                    </td>

                    {/* Form 11 Count */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {comp.form11_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCertType('FORM_11');
                            setSelectedCompanyId(comp.company_id);
                          }}
                          className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors cursor-pointer"
                          title="Drill down to Form 11 reports for this company"
                        >
                          {comp.form11_count}
                        </button>
                      ) : (
                        <span className="text-gray-300 font-mono">0</span>
                      )}
                    </td>

                    {/* Training Count */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {comp.training_count > 0 ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCertType('TRAINING');
                            setSelectedCompanyId(comp.company_id);
                          }}
                          className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer"
                          title="Drill down to Training certificates for this company"
                        >
                          {comp.training_count}
                        </button>
                      ) : (
                        <span className="text-gray-300 font-mono">0</span>
                      )}
                    </td>

                    {/* Total Count */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setSelectedCompanyId(comp.company_id)}
                        className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-slate-900 hover:bg-brand-600 text-white shadow-2xs transition-colors cursor-pointer"
                        title="Drill down to all certificates for this company"
                      >
                        {comp.total_count}
                      </button>
                    </td>

                    {/* Compliance Status Breakdown */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {comp.compliant_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/70">
                            <span>✓</span>
                            <span>{comp.compliant_count} Valid</span>
                          </span>
                        )}
                        {comp.expiring_soon_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200">
                            <span>⚠️</span>
                            <span>{comp.expiring_soon_count} Expiring</span>
                          </span>
                        )}
                        {comp.overdue_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-red-50 text-red-800 border border-red-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                            <span>{comp.overdue_count} Overdue</span>
                          </span>
                        )}
                        {comp.total_count === 0 && (
                          <span className="text-gray-400 text-[11px] italic">No certificates</span>
                        )}
                      </div>
                    </td>

                    {/* Latest Activity Date */}
                    <td className="py-3 px-3 text-gray-600 whitespace-nowrap text-xs">
                      {comp.latest_activity_date ? (
                        formatDateOnly(comp.latest_activity_date)
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Actions Column */}
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setSelectedCompanyId(comp.company_id)}
                        className="text-xs font-semibold text-white bg-brand-600 hover:bg-brand-700 px-3 py-1 rounded-md transition-colors cursor-pointer shadow-2xs"
                        title="Drill down to inspect certificates"
                      >
                        Drill Down →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={items.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
